/**
 * Settlement navigation configuration.
 * Mirrors the planning-side `navigationConfig.ts` but for the estate settlement workflow.
 *
 * Sidebar sections (from Figma):
 *   OVERVIEW  → Dashboard
 *   WORKFLOW  → 8 settlement phases
 *
 * SFrame numbering: Figma "Frame N" → SFrame{100+N}
 */

import type { NavItem, NavSubSection, NavGroup } from './navigationConfig';

/* ── re-export shared types so consumers can import from one place ── */
export type { NavItem, NavSubSection, NavGroup };

/* ── phase status helpers ── */
export type PhaseStatus = 'locked' | 'not-started' | 'in-progress' | 'complete';

export interface SettlementPhase {
  id: string;
  label: string;
  /** Overview SFrame route for this phase */
  path: string;
  /** Number of steps in this phase */
  stepCount: number;
  /** Status is resolved at runtime from settlement_progress */
  defaultStatus: PhaseStatus;
}

/**
 * All 8 workflow phases with their overview routes.
 * Step detail routes live under each phase (e.g., /sframe-124 through /sframe-142 for IAD).
 */
export const settlementPhases: SettlementPhase[] = [
  { id: 'onboarding',       label: 'Onboarding',                         path: '/sframe-111', stepCount: 4,  defaultStatus: 'not-started' },
  { id: 'iad',              label: 'Immediately After Death',             path: '/sframe-141', stepCount: 7,  defaultStatus: 'not-started' },
  { id: 'secure-inventory', label: 'Secure & Inventory Assets',          path: '/sframe-143', stepCount: 6,  defaultStatus: 'not-started' },
  { id: 'probate',          label: 'Initiate Probate & Legal Authorities', path: '/sframe-170', stepCount: 6, defaultStatus: 'not-started' },
  { id: 'notify',           label: 'Notify Interested Parties',          path: '/sframe-180', stepCount: 5,  defaultStatus: 'not-started' },
  { id: 'debts-taxes',      label: 'Settle Debts, Expenses & Taxes',     path: '/sframe-198', stepCount: 5, defaultStatus: 'not-started' },
  { id: 'distribute',       label: 'Distribute Assets',                  path: '/sframe-220', stepCount: 6,  defaultStatus: 'not-started' },
  { id: 'close',            label: 'Close the Estate',                   path: '/sframe-250', stepCount: 5,  defaultStatus: 'not-started' },
];

/**
 * Navigation structure consumed by SettlementSidebar.
 * Uses the same NavGroup/NavSubSection/NavItem shape as the planning-side nav
 * so the sidebar component can share rendering logic.
 */
export const settlementDashboardNav: NavGroup[] = [
  {
    title: 'Overview',
    sections: [
      {
        title: '',
        items: [
          { label: 'Dashboard', path: '/sframe-117', match: 'exact' },
        ],
      },
    ],
  },
  {
    title: 'Workflow',
    sections: [
      {
        title: '',
        items: settlementPhases.map((phase) => ({
          label: phase.label,
          path: phase.path,
          match: 'prefix' as const,
          disabled: phase.defaultStatus === 'locked',
        })),
      },
    ],
  },
  {
    title: 'Account',
    sections: [
      {
        title: '',
        items: [
          { label: 'Settings', path: '/settlement-settings', match: 'exact' },
          { label: 'Support', path: '/settlement-support', match: 'exact' },
        ],
      },
    ],
  },
];
